// Redirect Configuration
// This file contains configurable settings for user redirects and timers

export interface RedirectConfig {
    // Auto-redirect settings
    autoRedirectEnabled: boolean;
    autoRedirectDelaySeconds: number;
    
    // Manual redirect settings
    manualRedirectEnabled: boolean;
    manualRedirectText: string;
    
    // Visual settings
    showCountdown: boolean;
    countdownUpdateInterval: number; // milliseconds
    
    // Messages
    redirectMessage: string;
    countdownMessage: string;
    manualRedirectMessage: string;
}

// Default configuration
export const DEFAULT_REDIRECT_CONFIG: RedirectConfig = {
    // Auto-redirect after 60 seconds
    autoRedirectEnabled: true,
    autoRedirectDelaySeconds: 60,
    
    // Manual redirect option
    manualRedirectEnabled: true,
    manualRedirectText: 'Continue to Gaming',
    
    // Show countdown timer
    showCountdown: true,
    countdownUpdateInterval: 1000, // Update every second
    
    // User-friendly messages
    redirectMessage: 'You will be automatically redirected to continue your gaming experience',
    countdownMessage: 'Continuing in',
    manualRedirectMessage: 'Or click below to continue immediately'
};

// Registration success specific configuration
export const REGISTRATION_REDIRECT_CONFIG: RedirectConfig = {
    ...DEFAULT_REDIRECT_CONFIG,
    autoRedirectDelaySeconds: 60, // 60 seconds for registration
    manualRedirectText: 'Continue to Login',
    redirectMessage: 'You will be automatically redirected to the login page in 60 seconds',
    countdownMessage: 'Redirecting to login page in',
    manualRedirectMessage: 'Or click below to continue to login immediately'
};

// Authentication success specific configuration (for future use)
export const AUTH_REDIRECT_CONFIG: RedirectConfig = {
    ...DEFAULT_REDIRECT_CONFIG,
    autoRedirectDelaySeconds: 30, // 30 seconds for auth success
    manualRedirectText: 'Continue',
    redirectMessage: 'You will be automatically redirected',
    countdownMessage: 'Redirecting in',
    manualRedirectMessage: 'Or click below to continue immediately'
};

// Helper function to get configuration
export const getRedirectConfig = (type: 'registration' | 'auth' = 'registration'): RedirectConfig => {
    switch (type) {
        case 'registration':
            return REGISTRATION_REDIRECT_CONFIG;
        case 'auth':
            return AUTH_REDIRECT_CONFIG;
        default:
            return DEFAULT_REDIRECT_CONFIG;
    }
};

// Helper function to format time
export const formatTime = (seconds: number): string => {
    if (seconds <= 0) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    }
    
    return `${remainingSeconds}s`;
};

// Helper function to get countdown color based on remaining time
export const getCountdownColor = (seconds: number, totalSeconds: number): string => {
    const percentage = seconds / totalSeconds;
    
    if (percentage > 0.5) {
        return 'text-green-400'; // Green for more than 50% remaining
    } else if (percentage > 0.2) {
        return 'text-yellow-400'; // Yellow for 20-50% remaining
    } else {
        return 'text-red-400'; // Red for less than 20% remaining
    }
};

// Export types for use in components
// RedirectConfig is already exported above as interface
